"use client";

import { useEffect, useRef } from "react";

type Particle = {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  radius: number;
  color: string;
};

const COLORS = ["#b4ada2", "#8d867c", "#ece7df"];
const CONNECTION_DISTANCE = 110;

export function WireframeBackdrop() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    let width = 0;
    let height = 0;
    let particles: Particle[] = [];
    let animationFrame = 0;

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * devicePixelRatio;
      canvas.height = height * devicePixelRatio;
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

      const count = width < 768 ? 40 : 80;
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        velocityX: (Math.random() - 0.5) * 0.4,
        velocityY: (Math.random() - 0.5) * 0.4,
        radius: Math.random() * 2 + 0.8,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      }));
    };

    const animate = () => {
      context.clearRect(0, 0, width, height);
      for (const particle of particles) {
        particle.x = (particle.x + particle.velocityX + width) % width;
        particle.y = (particle.y + particle.velocityY + height) % height;
      }

      if (width >= 768) {
        for (let sourceIndex = 0; sourceIndex < particles.length; sourceIndex += 1) {
          const source = particles[sourceIndex];
          for (let targetIndex = sourceIndex + 1; targetIndex < particles.length; targetIndex += 1) {
            const target = particles[targetIndex];
            const deltaX = source.x - target.x;
            const deltaY = source.y - target.y;
            const distance = Math.hypot(deltaX, deltaY);
            if (distance >= CONNECTION_DISTANCE) {
              continue;
            }
            context.beginPath();
            context.moveTo(source.x, source.y);
            context.lineTo(target.x, target.y);
            context.strokeStyle = `rgb(180 173 162 / ${0.22 * (1 - distance / CONNECTION_DISTANCE)})`;
            context.lineWidth = 0.5;
            context.stroke();
          }
        }
      }

      for (const particle of particles) {
        context.beginPath();
        context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        context.fillStyle = particle.color;
        context.globalAlpha = 0.48;
        context.fill();
      }
      context.globalAlpha = 1;
      animationFrame = requestAnimationFrame(animate);
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden className="pointer-events-none absolute inset-0 z-0 size-full" />;
}